import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import './Experience.css';

const Experience = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const experiences = [
    {
      title: 'Intern — AI Agent Ops',
      company: 'OmniSolveAi — NICAT Incubated Startup',
      location: 'National Incubation Center for Aerospace Technologies (NICAT), Islamabad',
      period: 'Jul 2026 — Sept 2026',
      points: [
        'Built and deployed AI agent-based sales systems, including lead qualification and needs-assessment workflows.',
        'Developed task-specific AI agents to automate internal operations and support business workflows.',
        'Collaborated cross-functionally to design agent logic, test outputs, and iterate on automation reliability.'
      ]
    },
    {
      title: 'Intern — Healthcare IoT (SLM Integration)',
      company: 'Pak-Austria Fachhochschule',
      location: 'Haripur, Pakistan',
      period: 'Jun 2026 — Jul 2026',
      points: [
        'Integrated a computer vision pipeline to analyze radiology scans and predict cancer likelihood on an edge-deployed healthcare IoT solution.',
        'Built a patient vital-signs and activity monitoring system on an NVIDIA Jetson device, combining sensor data with ML models for real-time tracking.',
        'Ran a small language model on-device for lightweight, low-latency inference.'
      ]
    },
    {
      title: 'Apprentice — Computer Vision Lab',
      company: 'Sino-Pak Center for AI, PAF-IAST',
      location: 'Haripur, Pakistan',
      period: 'Jan 2025 — May 2025',
      points: [
        'Managed 120+ advanced AI and IoT devices — Jetson Orin Nano, Jetson AGX Orin, OAK-D cameras, Intel Neural Compute Stick, Azure Kinect, and high-end environmental and biomedical sensors.',
        'Conducted hands-on workshops on computer vision, embedded AI, and IoT for BS and MS students.',
        'Supported student projects involving Arduino, ESP32, Raspberry Pi, LiDAR, solenoid valves, and industrial automation components.'
      ]
    },
    {
      title: 'Apprentice — Intelligent Biomedical Applications Lab',
      company: 'Pak-Austria Fachhochschule',
      location: 'Haripur, Pakistan',
      period: 'Nov 2024 — Apr 2025',
      points: [
        'Developed an EMG-based muscle rehabilitation game — users with muscle damage or mobility impairments control gameplay with muscle signals.',
        'Built signal acquisition and processing pipelines for real-time movement detection using MyoWare EMG sensors, Arduino, and Python.',
        'Integrated ML gesture classification to adapt to different users in rehabilitation therapy.'
      ]
    },
    {
      title: 'President',
      company: 'PAF-IAST Welfare Society',
      location: 'Haripur, Pakistan',
      period: '2024 — Present',
      points: [
        'Lead a 32-member executive board and 250+ active volunteers across education, health, and disaster-relief programs.',
        'Executed 40+ welfare campaigns in 2024–2025 — food drives, blood donation, winter clothing, and international relief.',
        'Built partnerships with NGOs and government bodies (Rizq Foundation, Taba Chapter NUST, Circle Lahore) for joint projects and member internships.'
      ]
    }
  ];

  return (
    <section id="experience" className="section">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <div className="section-head">
            <span className="section-path">experience</span>
            <h2>Where I've worked</h2>
          </div>

          <div className="exp-list">
            {experiences.map((exp, index) => (
              <motion.article
                key={index}
                className="exp-item"
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
              >
                <div className="exp-period">
                  <span>{exp.period}</span>
                </div>
                <div className="exp-body">
                  <h3>{exp.title}</h3>
                  <p className="exp-company">
                    {exp.company} <span className="exp-sep">·</span> {exp.location}
                  </p>
                  <ul className="exp-points">
                    {exp.points.map((point, pointIndex) => (
                      <li key={pointIndex}>{point}</li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
